import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { Pagination } from "@/components/pagination";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type PurchaseStatus =
  | "pending_payment"
  | "paid"
  | "confirmed"
  | "completed"
  | "cancelled"
  | "refunded";

const ALL_STATUSES: PurchaseStatus[] = [
  "pending_payment",
  "paid",
  "confirmed",
  "completed",
  "refunded",
  "cancelled",
];

const DEFAULT_STATUSES: PurchaseStatus[] = ALL_STATUSES.filter(
  (s) => s !== "cancelled",
);

export const Route = createFileRoute("/_authenticated/purchases")({
  component: PurchasesPage,
});

const statusConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  pending_payment: { label: "Oczekuje płatności", variant: "outline" },
  paid: { label: "Opłacone", variant: "default" },
  confirmed: { label: "Potwierdzone", variant: "default" },
  completed: { label: "Zrealizowane", variant: "secondary" },
  cancelled: { label: "Anulowane", variant: "destructive" },
  refunded: { label: "Zwrócone", variant: "destructive" },
};

const paymentMethodLabels: Record<string, string> = {
  blik: "BLIK",
  paypo: "PayPo",
  twisto: "Twisto",
  onsite: "W salonie",
  test: "Testowa",
};

/** Audit trail event types, in the salon's language. */
const auditTypeLabels: Record<string, string> = {
  order_placed: "Złożono zamówienie",
  payment_initiated: "Rozpoczęto płatność",
  payment_succeeded: "Płatność zaksięgowana",
  payment_failed: "Płatność nieudana",
  payment_refunded: "Zwrot środków",
  paid_on_site: "Zapłacono w salonie",
  status_changed: "Zmiana statusu",
  order_cancelled: "Anulowano zamówienie",
  notification_sent: "Wysłano powiadomienie",
};

const auditActorLabels: Record<string, string> = {
  customer: "klient",
  staff: "personel",
  system: "system",
};

function formatPrice(cents: number) {
  return `${(cents / 100).toFixed(2)} zł`;
}

function formatDate(d: Date | string) {
  return new Date(d).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The order's audit trail. Answers "who paid for what, when and how much" —
 * including which confirmations went out — from the append-only log rather
 * than from the current row.
 */
function AuditTrailDialog({
  purchaseId,
  code,
  onClose,
}: {
  purchaseId: string;
  code: string;
  onClose: () => void;
}) {
  const { data, isLoading } = trpc.purchase.auditTrail.useQuery({ purchaseId });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Historia zamówienia {code}</DialogTitle>
          <DialogDescription>
            Zapis wymagany do rozliczeń i reklamacji. Wpisów nie da się
            edytować ani usunąć przed upływem okresu przechowywania.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Ładowanie...</p>
          )}

          {data?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Brak zapisów — to zamówienie powstało przed wdrożeniem dziennika.
            </p>
          )}

          <ol className="space-y-3">
            {data?.map((e) => (
              <li key={e.id} className="border-l-2 pl-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">
                    {auditTypeLabels[e.type] ?? e.type}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(e.occurredAt)} · {auditActorLabels[e.actor] ?? e.actor}
                  </span>
                </div>
                <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {e.amount !== null && <div>Kwota: {formatPrice(e.amount)}</div>}
                  {e.paymentMethod && (
                    <div>
                      Metoda:{" "}
                      {paymentMethodLabels[e.paymentMethod] ?? e.paymentMethod}
                    </div>
                  )}
                  {e.tpayTransactionId && (
                    <div className="font-mono">Tpay: {e.tpayTransactionId}</div>
                  )}
                  {e.messageId && (
                    <div className="font-mono break-all">
                      Wiadomość: {e.messageId}
                    </div>
                  )}
                  {e.payload && Object.keys(e.payload).length > 0 && (
                    <div className="font-mono break-all">
                      {JSON.stringify(e.payload)}
                    </div>
                  )}
                  <div>Przechowywane do: {formatDate(e.retainUntil)}</div>
                </div>
              </li>
            ))}
          </ol>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function PurchasesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] =
    useState<PurchaseStatus[]>(DEFAULT_STATUSES);
  const [auditFor, setAuditFor] = useState<{ id: string; code: string } | null>(
    null,
  );

  useEffect(() => {
    if (search === debouncedSearch) return;
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  const toggleStatus = (status: PurchaseStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
    setPage(1);
  };

  const statusesParam = useMemo(
    // Pass undefined when all are selected — avoids a no-op filter and keeps
    // the query cache key small.
    () =>
      selectedStatuses.length === ALL_STATUSES.length
        ? undefined
        : selectedStatuses,
    [selectedStatuses],
  );

  const { data, isLoading } = trpc.purchase.list.useQuery(
    {
      page,
      limit: 20,
      search: debouncedSearch || undefined,
      statuses: statusesParam,
    },
    { placeholderData: (prev) => prev },
  );
  const utils = trpc.useUtils();

  const updateStatus = trpc.purchase.updateStatus.useMutation({
    onSuccess: () => {
      utils.purchase.list.invalidate();
      toast.success("Status zaktualizowany");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Zakupy</h1>
        <p className="text-sm text-muted-foreground">
          {data?.total ?? 0} zakupów
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Szukaj po numerze zamówienia, emailu lub nazwisku..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {ALL_STATUSES.map((s) => {
          const active = selectedStatuses.includes(s);
          const label = statusConfig[s]?.label ?? s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              className="cursor-pointer"
            >
              <Badge variant={active ? "default" : "outline"}>{label}</Badge>
            </button>
          );
        })}
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Kod</th>
                <th className="px-4 py-3 font-medium">Klient</th>
                <th className="px-4 py-3 font-medium">Zabieg</th>
                <th className="px-4 py-3 font-medium">Kwota</th>
                <th className="px-4 py-3 font-medium">Płatność</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Data</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((p) => {
                const status = statusConfig[p.status] ?? { label: p.status, variant: "outline" as const };
                return (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-medium">{p.code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{p.customerName}</p>
                        <p className="text-xs text-muted-foreground">{p.customerEmail}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">{p.treatmentName}</td>
                    <td className="px-4 py-3">
                      <div>{formatPrice(p.amount)}</div>
                      {p.refundedAmount > 0 && (
                        <div className="text-xs text-orange-600">
                          {p.refundedAmount >= p.amount
                            ? "Zwrot: pełny"
                            : `Zwrot: ${formatPrice(p.refundedAmount)}`}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.paymentMethod ? (
                        <Badge
                          variant={
                            p.paymentMethod === "onsite" ? "outline" : "secondary"
                          }
                        >
                          {paymentMethodLabels[p.paymentMethod] ?? p.paymentMethod}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(p.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {p.status === "pending_payment" && (
                          <Button variant="default" size="sm" onClick={() => updateStatus.mutate({ purchaseId: p.id, status: "paid" })}>
                            Przyjmij płatność
                          </Button>
                        )}
                        {p.status === "paid" && (
                          <Button variant="default" size="sm" onClick={() => updateStatus.mutate({ purchaseId: p.id, status: "confirmed" })}>
                            Potwierdź
                          </Button>
                        )}
                        {p.status === "confirmed" && (
                          <Button variant="default" size="sm" onClick={() => updateStatus.mutate({ purchaseId: p.id, status: "completed" })}>
                            Zrealizuj
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAuditFor({ id: p.id, code: p.code })}
                        >
                          Historia
                        </Button>
                        {["pending_payment", "paid", "confirmed"].includes(p.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm("Anulować zamówienie?")) {
                                updateStatus.mutate({ purchaseId: p.id, status: "cancelled" });
                              }
                            }}
                          >
                            Anuluj
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Brak zakupów</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-3">
          <Pagination page={page} totalPages={data?.totalPages ?? 1} onPageChange={setPage} />
        </div>
      </Card>

      {auditFor && (
        <AuditTrailDialog
          purchaseId={auditFor.id}
          code={auditFor.code}
          onClose={() => setAuditFor(null)}
        />
      )}
    </div>
  );
}
